import { debounce } from 'lodash'
import { useCallback, useEffect, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'

// TODO onMarkerCreate is an intermediate solution at the moment. It might not look wrong but in terms of functionality is the actual marker rendering.
// Since this event will actually add the marker inside the map instance(render) it would be better to abstract out this event from marker component.
// I don't want this behavior be present to the end user of the Marker component, instead i'd like to use a direct dependency with the map instance to acquire the markersGroup singleton instance.
export interface MarkerPosition {
  lat: number
  lng: number
}

export interface MarkerProps {
  id: string
  position: MarkerPosition
  zIndex: number
  width?: number
  height?: number
  left?: number
  top?: number
  children: JSX.Element
  onMarkerCreate: (marker: H.map.Marker) => void
  onMouseLeave?: (id: string) => void
  onMouseEnter?: (id: string) => void
  onClick?: (id: string) => void
}

interface DisposableMarker extends H.map.Marker {
  dispose: () => void
}

interface DomMarkerData {
  id: string
  zIndex: number
  width: number
  height: number
  position: H.geo.IPoint
  iconElement?: SVGElement
  onAttachOnce?: () => void
}

function createDomMarker(
  position: H.geo.IPoint,
  iconMarkup: string,
  id: string,
  zIndex: number,
  width: number,
  height: number
): H.map.DomMarker {
  // "z-index" is not only supplied to marker options,
  // but also attached to its data, so it could be restored on highlighting removal
  const data: DomMarkerData = { height, id, position, width, zIndex }

  // DOM marker is used because:
  // * regular markers can't rasterize a referenced "<use />" children element
  // * markers are rescaling when getting highlighted, which requires a vector icon to avoid distortion
  return new H.map.DomMarker(position, {
    data,
    icon: createDomIcon(iconMarkup, data),
    zIndex,
  })
}

function createDomIcon(iconMarkup: string, data: DomMarkerData): H.map.DomIcon {
  return new H.map.DomIcon(iconMarkup, {
    onAttach: (iconElement, icon, marker) => {
      // "onAttachOnce" is read from "marker.getData()" because it doesn't exist on the local "data" variable,
      // but may exist on "marker.getData()", when it's accessed elsewhere during the marker's lifecycle
      const markerData = marker.getData() as DomMarkerData
      const { onAttachOnce } = markerData !== undefined ? markerData : { onAttachOnce: undefined }

      // it's important to assign "iconElement" before calling "onAttachOnce()"
      // because that callback may rely on the "iconElement"
      marker.setData({ ...data, iconElement })

      if (iconElement !== null) {
        iconElement.setAttribute('width', String(data.width))
        iconElement.setAttribute('height', String(data.height))
        const svgElement = iconElement as SVGElement
        'style' in iconElement && (svgElement.style.left = String(-data.width / 2))
        'style' in iconElement && (svgElement.style.top = String(-data.height))
      }

      if (onAttachOnce !== undefined) {
        onAttachOnce()
      }
    },
    onDetach: (iconElement, icon, marker) => marker.setData(data),
  })
}

export default function Marker({
  id,
  position,
  zIndex,
  width = 32,
  height = 42,
  children,
  onMarkerCreate,
  onMouseLeave,
  onMouseEnter,
  onClick,
}: MarkerProps): null {
  const markerRef = useRef<H.map.Marker | undefined>(undefined)

  const disposeMarker = useCallback(() => {
    const disposableMarker = markerRef?.current as DisposableMarker
    disposableMarker?.dispose()
  }, [])

  const updateMarker = useCallback(() => {
    const markerData = markerRef.current?.getData() as DomMarkerData
    const { iconElement } = markerData !== undefined ? markerData : ({} as DomMarkerData)
    if (iconElement !== undefined) {
      const childrenMarkup = ReactDOMServer.renderToStaticMarkup(children)
      const markerIcon = createDomIcon(childrenMarkup, {
        height: height,
        id: id,
        position: position,
        width: width,
        zIndex: zIndex,
      })

      markerRef.current?.setIcon(markerIcon)
      markerRef.current?.setZIndex(zIndex)
    }
  }, [children, height, id, position, width, zIndex])

  // Dispose the marker if the component unmounts
  useEffect(() => {
    return () => {
      disposeMarker()
    }
  }, [disposeMarker])

  // It creates the marker if the markerRef is undefined
  // If the key string is missing from Marker definition or isn't changing, we must check if the id, and position has changed.
  // In case of change we dispose and re-create the marker
  useEffect(() => {
    const isMarkerSet = markerRef.current !== undefined
    const markerData = markerRef.current?.getData() as DomMarkerData
    const markerInstanceChanged =
      markerData !== undefined &&
      (markerData.id !== id ||
        markerData.position.lat !== position.lat ||
        markerData.position.lng !== position.lng)
    if (!isMarkerSet || markerInstanceChanged) {
      isMarkerSet && disposeMarker()

      const childrenMarkup = ReactDOMServer.renderToStaticMarkup(children)
      const marker = createDomMarker(position, childrenMarkup, id, zIndex, width, height)
      onClick && marker.addEventListener('tap', onClick)
      const throttledHover = debounce((event: { type: string }) => {
        if (event.type === 'pointerleave') {
          onMouseLeave?.(id)
        }
        if (event.type === 'pointerenter') {
          onMouseEnter?.(id)
        }
      }, 100)
      marker.addEventListener('pointerenter', throttledHover)
      marker.addEventListener('pointerleave', throttledHover)
      onMarkerCreate(marker)
      markerRef.current = marker
    } else if (markerRef.current !== undefined) {
      updateMarker()
    }
  }, [
    children,
    disposeMarker,
    height,
    id,
    onClick,
    onMarkerCreate,
    onMouseEnter,
    onMouseLeave,
    position,
    updateMarker,
    width,
    zIndex,
  ])

  return null
}

// TODO for performanse purposes, we need to make the following line work.
// export default React.memo(Marker)
